import { z } from 'zod';

export const PAYWALL_SLUG_MAX_LENGTH = 40;
const PAYWALL_SLUG_PATTERN = /^[a-z0-9-]+$/;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CYRILLIC_TO_LATIN_MAP: Readonly<Record<string, string>> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'i',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
  і: 'i',
  ї: 'yi',
  є: 'ye',
  ґ: 'g',
};

export function normalizeRawPaywallSlug(value: string) {
  return value.trim().toLowerCase();
}

function trimTrailingHyphens(value: string) {
  return value.replace(/-+$/g, '');
}

function transliterateCyrillic(value: string) {
  return Array.from(value, (character) => CYRILLIC_TO_LATIN_MAP[character] ?? character).join('');
}

function normalizePaywallSlugCandidate(value: string) {
  const normalized = transliterateCyrillic(normalizeRawPaywallSlug(value))
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (normalized.length === 0) {
    return 'club';
  }

  return trimTrailingHyphens(normalized.slice(0, PAYWALL_SLUG_MAX_LENGTH)) || 'club';
}

export function createReadableClubSlug(value: string) {
  return normalizePaywallSlugCandidate(value);
}

export function withPaywallSlugSuffix(baseSlug: string, suffix: number) {
  const normalizedBase = normalizePaywallSlugCandidate(baseSlug);
  if (suffix <= 1) {
    return normalizedBase;
  }

  const suffixToken = `-${suffix}`;
  const availableBaseLength = Math.max(1, PAYWALL_SLUG_MAX_LENGTH - suffixToken.length);
  const trimmedBase = trimTrailingHyphens(normalizedBase.slice(0, availableBaseLength)) || 'club';
  return `${trimmedBase}${suffixToken}`;
}

export const PaywallSlugSchema = z
  .string()
  .transform(normalizeRawPaywallSlug)
  .refine((value) => value.length > 0, {
    message: 'Paywall slug is required',
  })
  .refine((value) => value.length <= PAYWALL_SLUG_MAX_LENGTH, {
    message: `Paywall slug must be at most ${PAYWALL_SLUG_MAX_LENGTH} characters`,
  })
  .refine((value) => PAYWALL_SLUG_PATTERN.test(value), {
    message: 'Paywall slug may contain only latin letters, digits, and hyphen',
  });

export const NullablePaywallSlugSchema = z.preprocess((value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== 'string') return value;

  const normalized = normalizeRawPaywallSlug(value);
  return normalized.length === 0 ? null : normalized;
}, PaywallSlugSchema.nullable());

export function normalizePaywallSlugInput(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;

  const normalized = normalizeRawPaywallSlug(value);
  return normalized.length > 0 ? normalized : null;
}

export function isUuidLikeClubReference(value: string): boolean {
  return UUID_PATTERN.test(value.trim());
}
